<template>
    <div class="flex flex-col w-full rounded-sm border border-border" :class="isHighlightAbo ? 'bg-error' : 'bg-success-100'">
        <!-- One-time purchase option -->
        <div
            class="relative cursor-pointer transition-all"
            @click="selectOption('onetime')"
        >
            <div class="p-4 bg-base-100 border-b border-border rounded-t-sm">
                <div class="flex items-center gap-3">
                    <input
                        type="radio"
                        name="purchase-type"
                        value="onetime"
                        :checked="selectedOption === 'onetime'"
                        class="radio radio-sm bg-white"
                    >
                    <span class="text-headline text-xl font-semibold">{{ t('product.abo.oneTimePurchase') }}</span>
                </div>
            </div>
        </div>

        <!-- Abo option -->
        <div
            class="relative cursor-pointer transition-all"
            @click="selectOption('abo')"
        >
            <div
                class="p-4 rounded-b-sm"
                :class="isHighlightAbo ? 'bg-error' : 'bg-success-100'"
            >
                <div class="flex items-start gap-2">
                    <input
                        type="radio"
                        name="purchase-type"
                        value="abo"
                        :checked="selectedOption === 'abo'"
                        class="radio radio-sm mt-[6px] bg-white"
                    >
                    <div class="flex flex-col md:gap-2">
                        <span
                            class="font-semibold text-xl"
                            :class="isHighlightAbo ? 'text-sale' : 'text-headline'"
                        >
                            {{ t('product.abo.buyAsAbo') }}
                        </span>
                        <span
                            class="text-lg"
                            :class="isHighlightAbo ? 'text-sale' : 'text-headline'"
                        >
                            <span class="font-semibold">{{ t('product.abo.benefitsFirst') }}</span>
                            {{ t('product.abo.benefitsSecond') }}
                        </span>

                        <!-- Abo options - only show when abo is selected -->
                        <div v-if="selectedOption === 'abo'" class="flex flex-col gap-3 md:gap-4 md:mt-4">
                            <!-- Frequency select -->
                            <div class="form-control md:flex md:gap-8">
                                <label :for="'frequency-select-' + product.id" class="label pl-0 pb-1">
                                    <span
                                        class="label-text font-semibold"
                                        :class="isHighlightAbo ? 'text-sale' : 'text-headline'"
                                    >
                                        {{ t('product.abo.frequency') }}
                                    </span>
                                </label>
                                <select
                                    :id="'frequency-select-' + product.id"
                                    v-model="selectedFrequency"
                                    class="select select-bordered w-full bg-white border-border !text-placeholder"
                                >
                                    <option v-for="option in frequencyOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>

                            <!-- Shipping day select -->
                            <div class="form-control md:flex md:gap-6">
                                <label :for="'shipping-day-select-' + product.id" class="label pl-0 pb-1">
                                    <span
                                        class="label-text font-semibold"
                                        :class="isHighlightAbo ? 'text-sale' : 'text-headline'"
                                    >
                                        {{ t('product.abo.shippingDay') }}
                                    </span>
                                </label>
                                <select
                                    :id="'shipping-day-select-' + product.id"
                                    v-model="selectedShippingDay"
                                    class="select select-bordered w-full bg-white border-border !text-placeholder"
                                >
                                    <option v-for="option in shippingDayOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>

                            <!-- Shipping info text -->
                            <p class="text-sm text-primary-content leading-relaxed">
                                {{ t('product.abo.shippingInfo') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import type { HblProduct } from '@/utils/types'
import { request as __request } from '@hubblecommerce/hubble/platforms/shopware/request'
import { OpenAPI } from '@hubblecommerce/hubble/platforms/shopware/api-client'

interface AboOption {
    value: string
    label: string
}

interface AboOptionsResponse {
    apiAlias: string
    intervals: AboOption[]
    shippingDays: AboOption[]
}

const { t } = useI18n()

const props = defineProps<{
    productData: HblProduct
}>()

const product = computed(() => props.productData)
const selectedOption = ref<'onetime' | 'abo'>('onetime')
const selectedFrequency = ref<string>('')
const selectedShippingDay = ref<string>('')

const frequencyOptions = ref<Array<{ value: string; label: string }>>([])
const shippingDayOptions = ref<Array<{ value: string; label: string }>>([])

const isHighlightAbo = computed(() => {
    return product.value?.customFields?.lg_abo_product_is_highlighted === true
})

const selectOption = (option: 'onetime' | 'abo') => {
    selectedOption.value = option
}

// Computed property to get abo payload for cart
const aboPayload = computed(() => {
    if (selectedOption.value === 'abo' && selectedFrequency.value && selectedShippingDay.value) {
        return {
            lg_abo_product_shipping_day: selectedShippingDay.value,
            lg_abo_product_interval: selectedFrequency.value
        }
    }
    return null
})

// Expose values to parent component
defineExpose({
    selectedOption,
    aboPayload
})

// Fetch abo options from Shopware API
async function fetchAboOptions() {
    try {
        const response = await __request(OpenAPI, {
            method: 'GET',
            url: '/lg-abo/options'
        }) as AboOptionsResponse

        // Populate interval/frequency options
        if (response?.intervals && Array.isArray(response.intervals)) {
            frequencyOptions.value = response.intervals
            // Set default to first option ("01" - Jede Woche) if available
            const firstInterval = response.intervals[0]
            if (firstInterval && !selectedFrequency.value) {
                selectedFrequency.value = firstInterval.value
            }
        }

        // Populate shipping day options
        if (response?.shippingDays && Array.isArray(response.shippingDays)) {
            shippingDayOptions.value = response.shippingDays
            // Set default to "DO" (Donnerstag) if available
            const thursday = response.shippingDays.find((day: any) => day.value === 'DO')
            if (thursday && !selectedShippingDay.value) {
                selectedShippingDay.value = thursday.value
            }
        }
    } catch (error) {
        console.error('Failed to fetch abo options:', error)
    }
}

onMounted(() => {
    fetchAboOptions()
})
</script>
